import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, hora, sufijoUnidad } from "../lib/formato";
import { useIdioma, type Idioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface ProductoCarta {
  id: string;
  nombre: string;
  genetica: string;
  thc: number | null;
  cbd: number | null;
  precioCentimos: number;
  precioBaseCentimos: number;
  descuentoTexto: string | null;
}

interface Carta {
  club: { nombre: string; logoUrl: string | null; idioma?: Idioma };
  mensaje: string | null;
  actualizada: string;
  categorias: { id: string; nombre: string; color: string; unidadMedida: string; productos: ProductoCarta[] }[];
}

/**
 * Carta para una pantalla del local. Se abre con el enlace que da el club,
 * se refresca sola y esta pensada para verse de lejos.
 */
export default function CartaApp() {
  const { t, aplicarPorDefecto } = useIdioma();
  const partes = window.location.pathname.replace(/^\/carta\/?/, "").split("/");
  const slug = partes[0] ?? "";
  const token = partes[1] ?? "";

  const carta = useQuery({
    queryKey: ["carta", slug, token],
    queryFn: () => api<Carta>(`/carta/${encodeURIComponent(slug)}/${encodeURIComponent(token)}`),
    refetchInterval: 60_000,
    /*
     * En un televisor la pestana no esta "enfocada" casi nunca, y sin esto la
     * consulta periodica se para: la carta se quedaba congelada en los precios
     * y el stock de cuando se encendio la pantalla.
     */
    refetchIntervalInBackground: true,
    retry: false,
  });

  const delClub = carta.data?.club.idioma;
  useEffect(() => aplicarPorDefecto(delClub), [delClub, aplicarPorDefecto]);

  if (carta.isLoading) return <div className="carta-aviso">{t("carta.cargando")}</div>;

  /*
   * El aviso de error solo cuando no hay NADA que ensenar.
   *
   * Antes bastaba con que fallara una actualizacion —un corte de wifi de diez
   * segundos— para que la pantalla del local cambiara la carta por un
   * "Carta no disponible" delante de todo el mundo, teniendo los datos buenos
   * en memoria. Ahora se mantiene lo ultimo que se supo y se avisa abajo, en
   * pequeno, de que puede estar desfasado.
   */
  if (!carta.data) {
    return (
      <div className="carta-aviso">
        <h1>{t("carta.noDisponible")}</h1>
        <p>{t("carta.noDisponibleAyuda")}</p>
      </div>
    );
  }

  const { club, mensaje, categorias, actualizada } = carta.data;

  return (
    <div className="carta">
      <header className="carta-cabecera">
        <h1>{club.nombre}</h1>
        {mensaje && <p>{mensaje}</p>}
      </header>

      {categorias.length === 0 ? (
        <div className="carta-aviso">
          <p>{t("carta.sinProducto")}</p>
        </div>
      ) : (
        <div className="carta-columnas">
          {categorias.map((categoria) => (
            <section key={categoria.id} className="carta-categoria">
              <h2 style={{ borderColor: categoria.color }}>{categoria.nombre}</h2>
              <ul>
                {categoria.productos.map((p) => (
                  <li key={p.id}>
                    <div className="carta-nombre">
                      {p.nombre}
                      <span className="carta-detalle">
                        {[
                          // «NA» es «no consta»: en la pantalla del local se
                          // queda en blanco, no se pone un guion.
                          p.genetica === "NA" ? "" : t(`genetica.${p.genetica}` as Clave),
                          p.thc ? `THC ${p.thc}%` : "",
                          p.cbd ? `CBD ${p.cbd}%` : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    <div className="carta-precio">
                      {p.descuentoTexto && <span className="carta-tachado">{creditos(p.precioBaseCentimos)}</span>}
                      {creditos(p.precioCentimos)}
                      <span className="carta-unidad">/{sufijoUnidad(categoria.unidadMedida)}</span>
                      {p.descuentoTexto && <span className="carta-oferta">{p.descuentoTexto}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <footer className="carta-pie">
        {carta.isError
          ? t("carta.pieSinConexion", { hora: hora(actualizada) })
          : t("carta.pie", { hora: hora(actualizada) })}
      </footer>
    </div>
  );
}
